// #region imports

// --- Angular
import { BrowserModule } from '@angular/platform-browser';
import { ErrorHandler, NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CookieService } from 'ngx-cookie-service';
import {
  HttpClientModule,
  HttpClient,
  HTTP_INTERCEPTORS
} from '@angular/common/http';
import { LayoutModule } from '@angular/cdk/layout';

// --- 3rd Party
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { JL } from 'jsnlog';

// --- HS Code
import { environment } from '../environments/environment';
import { envServiceProvider } from './core/services/env.service.provider';
import * as c from './';
import { AppRoutingModule } from './app-routing.module';
import { CoreModule } from './core/core.module';
import { AddCsrfInterceptorService, LanguageInterceptorService, NetWorkInterceptorService, StandardHeadersService, TokenInterceptorService } from './core/interceptors';
import { SharedModule } from './shared/shared.module';
import {
  BehaviourTutorialBaseService,
  BehaviourTutorialService,
  CommunicationsBaseService,
  CommunicationsService,
  CompareReportBaseService,
  CompareReportService,
  DemographicsBaseService,
  DemographicsService,
  EnrichedUserDetailsBaseService,
  EnrichedUserDetailsService,
  ImpersonationValidationBaseService,
  ImpersonationValidationService,
  InteractionAuthorizationBaseService,
  InteractionAuthorizationService,
  InteractiveIndicatorBaseService,
  InteractiveIndicatorService,
  InvitationBaseService,
  InvitationService,
  JournalingBaseService,
  JournalingService,
  LanguageDomBaseService,
  LanguageDomService,
  LanguagesBaseService,
  LanguagesService,
  LoggerService,
  QuadrantsBaseService,
  QuadrantsService,
  ParticipantGroupsBaseService,
  ParticipantGroupsService,
  ParticipantValidationBaseService,
  ParticipantValidationService,
  ProfileBaseService,
  ProfileService,
  QuestionsBaseService,
  QuestionsService,
  RegistrationValidationBaseService,
  RegistrationValidationService,
  ReportsBaseService,
  ReportsService,
  RespondentDeclineBaseService,
  RespondentDeclineService,
  RespondentReminderBaseService,
  RespondentReminderService,
  RespondentsBaseService,
  RespondentsService,
  RespondentSurveyBaseService,
  RespondentSurveyService,
  RespondentValidationBaseService,
  RespondentValidationService,
  ResponseBaseService,
  ResponseService,
  ResultsBaseService,
  ResultsService,
  SelfRegistrationBaseService,
  SelfRegistrationMockService,
  SurveyBaseService,
  SurveyBreakBaseService,
  SurveyBreakService,
  SurveyService,
  TermsAndConditionsBaseService,
  TermsAndConditionsService,
  UserDemographicsBaseService,
  UserDemographicsService,
  UserDetailsBaseService,
  UserDetailsService,
  UserNotificationBaseService,
  UserNotificationService,
  WelcomeBaseService,
  WelcomeService,
  SelfRegistrationService,
} from './core/services';
import { ErrorNotfoundComponent } from './error/error-notfound.component';
import { ErrorComponent } from './error/error.component';
import { ErrorTestComponent } from './error/error-test.component';
import { SignoutRedirectCallbackComponent } from './signout-redirect-callback/signout-redirect-callback.component';
import { LogLevels } from './models';

// #endregion

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/app/', `.json?decache=${Math.floor(Math.random()*10000)}`);
}

export function getLogLevel(): number {
  switch (environment.authSettings.authLogger.level) {
    case LogLevels.DEBUG: return JL.getDebugLevel();
    case LogLevels.INFO: return JL.getInfoLevel();
    case LogLevels.WARN: return JL.getWarnLevel();
    case LogLevels.ERROR: return JL.getErrorLevel();
    default: return JL.getOffLevel();
  }
}

// JSNLog
const jsnLogUrl: string = `${environment.apiConfig.surveyApiUrl.replace('/api', '/jsnlog.logger')}`;
const jlLogLevel = getLogLevel();
JL.setOptions({
  'defaultAjaxUrl': jsnLogUrl,
  'level': jlLogLevel
} as JL.JSNLogOptions);
export class UncaughtExceptionHandler implements ErrorHandler {
  handleError(error: any) {
      JL().fatalException('EE - Uncaught Exception', error);
  }
}

@NgModule({
  declarations: [
    c.AppComponent,
    ErrorNotfoundComponent,
    ErrorComponent,
    ErrorTestComponent,
    SignoutRedirectCallbackComponent,
  ],
  imports: [
    AppRoutingModule,
    BrowserModule,
    BrowserAnimationsModule,
    CoreModule,
    HttpClientModule,
    SharedModule,
    LayoutModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient]
      },
      isolate: true
    }),
  ],
  providers: [
    // Logging - JSNLog
    {
      provide: ErrorHandler, useClass: UncaughtExceptionHandler
    },
    {
      provide: 'JSNLOG',
      useValue: JL
    },

    // Others
    envServiceProvider,
    CookieService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptorService,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: StandardHeadersService,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AddCsrfInterceptorService,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NetWorkInterceptorService,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageInterceptorService,
      multi: true
    },
    {
      provide: BehaviourTutorialBaseService,
      useClass: BehaviourTutorialService,
    },
    {
      provide: CommunicationsBaseService,
      useClass: CommunicationsService,
    },
    {
      provide: CompareReportBaseService,
      useClass: CompareReportService,
    },
    {
      provide: DemographicsBaseService,
      useClass: DemographicsService,
    },
    {
      provide: EnrichedUserDetailsBaseService,
      useClass: EnrichedUserDetailsService,
    },
    {
      provide: ImpersonationValidationBaseService,
      useClass: ImpersonationValidationService,
    },
    {
      provide: InteractionAuthorizationBaseService,
      useClass: InteractionAuthorizationService,
    },
    {
      provide: InteractiveIndicatorBaseService,
      useClass: InteractiveIndicatorService,
    },
    {
      provide: InvitationBaseService,
      useClass: InvitationService,
    },
    {
      provide: JournalingBaseService,
      useClass: JournalingService,
    },
    {
      provide: LanguageDomBaseService,
      useClass: LanguageDomService,
    },
    {
      provide: LanguagesBaseService,
      useClass: LanguagesService
    },
    {
      provide: LoggerService
    },
    {
      provide: QuadrantsBaseService,
      useClass: QuadrantsService
    },
    {
      provide: ParticipantGroupsBaseService,
      useClass: ParticipantGroupsService
    },
    {
      provide: ParticipantValidationBaseService,
      useClass: ParticipantValidationService
    },
    {
      provide: ProfileBaseService,
      useClass: ProfileService
    },
    {
      provide: QuestionsBaseService,
      useClass: QuestionsService
    },
    {
      provide: RegistrationValidationBaseService,
      useClass: RegistrationValidationService
    },
    {
      provide: ReportsBaseService,
      useClass: ReportsService
    },
    {
      provide: RespondentDeclineBaseService,
      useClass: RespondentDeclineService
    },
    {
      provide: RespondentReminderBaseService,
      useClass: RespondentReminderService
    },
    {
      provide: RespondentsBaseService,
      useClass: RespondentsService
    },
    {
      provide: RespondentSurveyBaseService,
      useClass: RespondentSurveyService
    },
    {
      provide: RespondentValidationBaseService,
      useClass: RespondentValidationService
    },
    {
      provide: ResponseBaseService,
      useClass: ResponseService
    },
    {
      provide: ResultsBaseService,
      useClass: ResultsService
    },
    {
      provide: SelfRegistrationBaseService,
      useClass: SelfRegistrationService
    },
    {
      provide: SurveyBaseService,
      useClass: SurveyService
    },
    {
      provide: SurveyBreakBaseService,
      useClass: SurveyBreakService
    },
    {
      provide: TermsAndConditionsBaseService,
      useClass: TermsAndConditionsService
    },
    {
      provide: UserDemographicsBaseService,
      useClass: UserDemographicsService
    },
    {
      provide: UserDetailsBaseService,
      useClass: UserDetailsService
    },
    {
      provide: UserNotificationBaseService,
      useClass: UserNotificationService
    },
    {
      provide: WelcomeBaseService,
      useClass: WelcomeService
    }
  ],
  exports: [
  ],
  bootstrap: [c.AppComponent]
})
export class AppModule { }
